/**
 * Shared shapes + guards for errors surfaced by the axios data layer.
 *
 * Caught values are `unknown`; narrow them with `isApiError` (or read a message
 * with `getApiErrorMessage`) instead of typing catch clauses as `any`.
 */

import i18n from '../i18n';
import { formatNumber } from './format';

/**
 * The text of an error the server sent, in the reader's language.
 *
 * Every error a user can see carries a stable `code` and the `params` its sentence
 * needs, beside the English `detail` (backend src/errors.py):
 * `{ detail: "Cat not found", code: "catNotFound", params: {} }`. The text is
 * `serverErrors.<code>` in locales/<lang>.json, whose English is the backend's own.
 * A body with no code, or a code this build does not know, gets `fallback` — the
 * existing generic message for the situation — never the server's English.
 */
export function serverErrorText(body: unknown, fallback: string): string {
  if (!body || typeof body !== 'object') return fallback;
  const { code, params } = body as { code?: unknown; params?: unknown };
  if (typeof code !== 'string' || !code) return fallback;
  const values: Record<string, unknown> = {};
  if (params && typeof params === 'object') {
    for (const [name, value] of Object.entries(params as Record<string, unknown>)) {
      // i18next picks the plural form from a numeric `count`.
      values[name] = typeof value === 'number' && name !== 'count' ? formatNumber(value) : value;
    }
  }
  const key = `serverErrors.${code}`;
  return i18n.exists(key, values) ? i18n.t(key, values) : fallback;
}

/**
 * The error shape our HTTP layer produces — either a raw axios error (with
 * `response.data.detail` from the backend) or a plain `Error` the response
 * interceptor threw, which may carry extra fields it set (e.g. `status` /
 * `existingLogId` on a 409). Every field is optional, so a narrowed `unknown`
 * can be read safely via optional chaining.
 */
export interface ApiError {
  message?: string;
  /** Set by the 409 branch of the response interceptor. */
  status?: number;
  existingLogId?: string | null;
  response?: {
    status?: number;
    data?: {
      detail?: unknown;
      [key: string]: unknown;
    };
  };
  config?: {
    headers?: Record<string, unknown>;
  };
}

/** Narrow an unknown caught value to the {@link ApiError} shape. */
export function isApiError(error: unknown): error is ApiError {
  return typeof error === 'object' && error !== null;
}

/**
 * Best-effort human-readable message for a caught value: the server's error by its
 * code (`serverErrorText`, `fallback` for an unknown one), else the message of an
 * Error the response interceptor already wrote in the reader's language, else
 * `fallback`.
 */
export function getApiErrorMessage(error: unknown, fallback: string): string {
  if (isApiError(error)) {
    if (error.response?.data) return serverErrorText(error.response.data, fallback);
    if (typeof error.message === 'string' && error.message) return error.message;
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

/**
 * A backend validation error, as the response interceptor rejects it: `message` is
 * the backend's own reason in plain words — what the owner should read first — and
 * `technical` is the raw detail, for a "Technical details" disclosure (finding 16).
 */
export interface ValidationError extends Error {
  status: number;
  technical: string;
}

export function isValidationError(error: unknown): error is ValidationError {
  return (
    error instanceof Error &&
    typeof (error as Partial<ValidationError>).technical === 'string' &&
    ((error as Partial<ValidationError>).status === 400 || (error as Partial<ValidationError>).status === 422)
  );
}

/**
 * The field's name in the reader's language: "weight_at_diagnosis" →
 * errors.fields.weightAtDiagnosis ("Peso no diagnóstico"). A field with no entry
 * is spelled out from its name in English ("Weight at diagnosis") and named not at
 * all in another language, rather than in English beside a translated message.
 */
const FIELD_LABELS: Record<string, () => string> = {
  email: () => i18n.t('errors.fields.email'),
  password: () => i18n.t('errors.fields.password'),
  full_name: () => i18n.t('errors.fields.fullName'),
  name: () => i18n.t('errors.fields.name'),
  date: () => i18n.t('errors.fields.date'),
  weight_kg: () => i18n.t('errors.fields.weightKg'),
  weight_at_diagnosis: () => i18n.t('errors.fields.weightAtDiagnosis'),
  date_of_birth: () => i18n.t('errors.fields.dateOfBirth'),
  diagnosis_date: () => i18n.t('errors.fields.diagnosisDate'),
  notes: () => i18n.t('errors.fields.notes'),
};

function fieldLabel(loc: unknown): string | null {
  if (!Array.isArray(loc)) return null;
  const last = [...loc].reverse().find((p) => typeof p === 'string' && p !== 'body');
  if (typeof last !== 'string') return null;
  const label = FIELD_LABELS[last];
  if (label) return label();
  if (!i18n.language?.startsWith('en')) return null;
  const words = last.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Validation messages that read whole without the field's name in front. */
const STANDALONE_VALIDATION_CODES = new Set(['validation.invalidEmail']);

/**
 * The backend's reason in plain words. FastAPI sends pydantic's array of
 * `{loc, msg, type, code, params}`; each item is rendered from its code
 * (`serverErrorText`), and an item without a known code is left out. Our own
 * validators' messages are written for owners and stand alone; pydantic's built-in
 * messages (`validation.*`: "Input should be less than or equal to 30") name no
 * field, so the field is put in front. With nothing left, the generic message.
 */
export function plainValidationMessage(detail: unknown): string {
  if (!Array.isArray(detail) || detail.length === 0) return i18n.t('errors.checkInput');
  const parts = (detail as Array<{ loc?: unknown; code?: unknown; params?: unknown }>).map((d) => {
    const msg = serverErrorText(d, '').trim();
    if (!msg) return null;
    if (
      typeof d.code !== 'string' ||
      !d.code.startsWith('validation.') ||
      STANDALONE_VALIDATION_CODES.has(d.code)
    ) {
      return msg;
    }
    const label = fieldLabel(d.loc);
    return label
      ? i18n.t('errors.fieldMessage', { field: label, message: `${msg.charAt(0).toLowerCase()}${msg.slice(1)}` })
      : msg;
  });
  const sentences = [...new Set(parts.filter((p): p is string => Boolean(p)))].map((p) =>
    /[.!?]$/.test(p) ? p : `${p}.`,
  );
  return sentences.join(' ') || i18n.t('errors.checkInput');
}
